import { Injectable } from '@angular/core';
import {
  AbstractMesh,
  Color3,
  Scene,
  SceneLoader,
  StandardMaterial,
  TransformNode,
  Vector3,
  MeshBuilder,
  DynamicTexture,
  Mesh,
  AssetContainer,
} from '@babylonjs/core';

export interface LiveUserAssetConfig {
  /** Path to human GLB directory (must end with /) */
  glbPath: string;
  /** Human GLB filename (legacy fallback) */
  glbFile: string;
  /** Target human height in world units. Auto-calculated from room if 0. */
  humanHeight: number;
  /** Fraction of room height the human should occupy (0–1). Used when humanHeight is 0. */
  roomHeightFraction: number;
  /** Gap above avatar head before cluster sphere centre (world units). */
  clusterHeadOffset: number;
  /** Cluster sphere radius (world units). */
  clusterSphereRadius: number;
  /** Minimum scale clamp */
  minScale: number;
  /** Maximum scale clamp */
  maxScale: number;
  /** Inner margin fraction (0–1) to keep humans away from room edges */
  placementMargin: number;
  /** Optional diffuse colour override (null = use GLB default) */
  color: Color3 | null;
  /** When true, always render a cluster sphere + label for any room with occupancy > 0,
   * overriding the clusterThreshold. */
  showClusters?: boolean;
  /** Rooms with occupancy > clusterThreshold render as a cluster sphere + label
   * instead of individual avatars. Rooms that drop to ≤ threshold switch back to individual avatars automatically.*/
  clusterThreshold: number;
}

const DEFAULT_CONFIG: LiveUserAssetConfig = {
  glbPath: '/assets/3d/humans/',
  glbFile: 'male.glb',
  humanHeight: 0,
  roomHeightFraction: 0.80,
  clusterHeadOffset: 0.35,
  clusterSphereRadius: 0.45,
  minScale: 0.001,
  maxScale: 10,
  placementMargin: 0.12,
  color: null,
  showClusters: true,
  clusterThreshold: 15,
};

export interface RoomOccupancy {
  roomId: string;
  count: number;
  statusCounts?: { danger: number; safe: number; unknown: number };
}

interface RoomAssetState {
  clones: AbstractMesh[];
  parentNode: TransformNode;
}

interface ClusterState {
  sphere: Mesh;
  label: Mesh;
  labelTexture: DynamicTexture;
  /** Representative avatar standing beneath the sphere */
  avatar: AbstractMesh | null;
  count: number;
  /** Last layout footprint for reposition after floor reparent */
  layoutCenter: Vector3;
  layoutScale: number;
  layoutCeilingY?: number;
}

@Injectable()
export class LiveUserAssetService {
  private scene: Scene | null = null;
  private humanTemplate: AbstractMesh | null = null;
  private humanTemplateMeshes: AbstractMesh[] = [];
  private templateLoading: Promise<void> | null = null;
  private roomStates = new Map<string, RoomAssetState>();
  private roomClusters = new Map<string, ClusterState>();
  private roomStatusCounts = new Map<string, { danger: number; safe: number; unknown: number }>();
  private templateNativeHeight = 1;
  private templateBottomLocalY = 0;
  private config: LiveUserAssetConfig = { ...DEFAULT_CONFIG };
  private colorMaterial: StandardMaterial | null = null;

  private avatarContainers: AssetContainer[] = [];
  private avatarContainerHeights = new Map<AssetContainer, number>();
  private avatarContainerBottoms = new Map<AssetContainer, number>();
  private avatarFiles = [
    'woman.glb',
    'male1.glb',
  ];

  init(scene: Scene, config?: Partial<LiveUserAssetConfig>): void {
    if (this.scene === scene && !config) return;
    if (this.scene !== scene) {
      for (const roomId of Array.from(this.roomStates.keys())) this.clearRoom(roomId);
      this.roomStates.clear();
      for (const roomId of Array.from(this.roomClusters.keys())) this.removeCluster(roomId);
      this.roomClusters.clear();
      this.roomStatusCounts.clear();
      this.scene = scene;
    }
    if (config) this.configure(config);
  }

  configure(overrides: Partial<LiveUserAssetConfig>): void {
    this.config = { ...this.config, ...overrides };
    if (overrides.color !== undefined) this.applyColorToExistingClones();
  }

  getConfig(): Readonly<LiveUserAssetConfig> {
    return { ...this.config };
  }

  /** Recompute avatar + cluster positions after floor-group reparent (world → floor-local). */
  refreshLayouts(roomMeshIndex: Map<string, AbstractMesh[]>): void {
    if (!this.scene) {
      return;
    }

    for (const [roomId, cluster] of this.roomClusters.entries()) {
      const meshes = roomMeshIndex.get(roomId);
      const state = this.roomStates.get(roomId);
      if (!meshes?.length || !state) {
        continue;
      }
      const bounds = this.computeRoomBounds(meshes);
      if (!bounds) {
        continue;
      }
      const standY = this.resolveStandWorldY(meshes, bounds);
      const center = this.clampCenterToRoomFootprint(bounds, standY);
      const scale = this.computeHumanScale(bounds);

      if (!cluster.avatar || cluster.avatar.isDisposed()) {
        cluster.avatar = this.createHumanClone(roomId, -1, scale);
        if (cluster.avatar) {
          cluster.avatar.parent = state.parentNode;
          cluster.avatar.setEnabled(true);
          this.enableChildMeshes(cluster.avatar);
        }
      }

      if (!cluster.avatar) {
        this.removeCluster(roomId);
        continue;
      }

      cluster.layoutCeilingY = bounds.max.y;
      this.layoutClusterVisuals(cluster, center, scale, cluster.avatar);
    }

    for (const [roomId, state] of this.roomStates.entries()) {
      if (this.roomClusters.has(roomId)) {
        continue;
      }
      const meshes = roomMeshIndex.get(roomId);
      if (!meshes?.length || !state.clones.length) {
        continue;
      }
      const bounds = this.computeRoomBounds(meshes);
      if (!bounds) {
        continue;
      }
      const standY = this.resolveStandWorldY(meshes, bounds);
      const scale = this.computeHumanScale(bounds);
      const positions = this.scatterFromRoomCenter(bounds, state.clones.length, standY);
      state.clones.forEach((clone, index) => {
        const pos = positions[index] ?? positions[positions.length - 1];
        const perAvatarBottomY = (clone as any)._avatarBottomLocalY ?? this.templateBottomLocalY;
        const perAvatarFeetOffset = -perAvatarBottomY * (clone.scaling?.y ?? scale);
        clone.position = new Vector3(pos.x, pos.y + perAvatarFeetOffset, pos.z);
      });
    }
  }

  async syncOccupancy(
    occupancies: RoomOccupancy[],
    roomMeshIndex: Map<string, AbstractMesh[]>
  ): Promise<void> {
    if (!this.scene) return;

    await this.ensureTemplate();
    if (!this.humanTemplate || !this.scene) return;

    const desiredByRoom = new Map<string, number>();
    for (const occ of occupancies) {
      if (occ.count > 0) {
        desiredByRoom.set(occ.roomId, occ.count);
        if (occ.statusCounts) {
          this.roomStatusCounts.set(occ.roomId, occ.statusCounts);
        }
      }
    }

    // Clear rooms that no longer have occupancy
    for (const roomId of Array.from(this.roomStates.keys())) {
      if (!desiredByRoom.has(roomId)) this.clearRoom(roomId);
    }

    // Sync each occupied room
    for (const [roomId, count] of desiredByRoom) {
      const meshes = roomMeshIndex.get(roomId);
      if (!meshes?.length) continue;
      this.syncRoom(roomId, count, meshes);
    }
  }

  dispose(): void {
    for (const roomId of Array.from(this.roomStates.keys())) this.clearRoom(roomId);
    this.roomStates.clear();

    for (const container of this.avatarContainers) {
      container.dispose();
    }
    this.avatarContainers = [];
    this.avatarContainerHeights.clear();
    this.avatarContainerBottoms.clear();

    this.humanTemplate = null;
    this.templateNativeHeight = 1;
    this.templateBottomLocalY = 0;
    this.templateLoading = null;
    this.colorMaterial?.dispose();
    this.colorMaterial = null;
    this.scene = null;
  }

  private async ensureTemplate(): Promise<void> {
    if (this.humanTemplate) return;
    if (this.templateLoading) { await this.templateLoading; return; }
    this.templateLoading = this.loadTemplate();
    await this.templateLoading;
  }

  private async loadTemplate(): Promise<void> {
    if (!this.scene) return;
    this.avatarContainers = [];
    this.avatarContainerHeights.clear();
    this.avatarContainerBottoms.clear();

    for (const file of this.avatarFiles) {
      try {
        const container = await SceneLoader.LoadAssetContainerAsync(
          this.config.glbPath, file, this.scene
        );
        this.avatarContainers.push(container);

        const height = this.measureTemplateHeight(container.meshes);
        const bottom = this.measureTemplateBottomLocalY(container.meshes);
        this.avatarContainerHeights.set(container, height);
        this.avatarContainerBottoms.set(container, bottom);
      } catch (err) {
        console.error(`[live-user-asset] Failed to load avatar GLB: ${file}`, err);
      }
    }

    if (this.avatarContainers.length > 0) {
      const first = this.avatarContainers[0];
      this.humanTemplate = {} as any;
      let measuredHeight = this.avatarContainerHeights.get(first) || 1.8;
      for (const container of this.avatarContainers) {
        const h = this.avatarContainerHeights.get(container) || measuredHeight;
        measuredHeight = Math.min(measuredHeight, h);
      }
      this.templateNativeHeight = measuredHeight > 0.001 ? measuredHeight : 1.8;
      this.templateBottomLocalY = this.avatarContainerBottoms.get(first) || 0;
    } else {
      this.templateLoading = null;
    }
  }

  /** Main decision point per room:
   *  count > threshold  → cluster mode (sphere + label + one representative avatar)
   *  count ≤ threshold  → individual avatar mode
   * Transitions between modes are handled cleanly in both directions. */
  private syncRoom(
    roomId: string,
    desiredCount: number,
    roomMeshes: AbstractMesh[]
  ): void {
    // Ensure a parent node exists for avatar clones
    let state = this.roomStates.get(roomId);
    if (!state) {
      const parentNode = new TransformNode(`liveUser_room_${roomId}`, this.scene!);
      parentNode.parent = null;
      parentNode.position = Vector3.Zero();
      state = { clones: [], parentNode };
      this.roomStates.set(roomId, state);
    }

    const bounds = this.computeRoomBounds(roomMeshes);
    if (!bounds) return;

    const standY = this.resolveStandWorldY(roomMeshes, bounds);
    const center = this.clampCenterToRoomFootprint(bounds, standY);

    const forceCluster = this.config.showClusters && desiredCount > 0;
    const useCluster = forceCluster || desiredCount > this.config.clusterThreshold;
    if (useCluster) {
      // Remove any individual avatars that were previously rendered
      this.clearAvatarClones(state);

      const existing = this.roomClusters.get(roomId);
      if (existing) {
        this.updateClusterCount(roomId, existing, desiredCount, center, bounds, state);
      } else {
        this.createCluster(roomId, desiredCount, center, bounds, state);
      }
    } else {
      // Remove any cluster that was previously rendered
      this.removeCluster(roomId);

      const currentCount = state.clones.length;
      if (currentCount === desiredCount) return;

      // Trim excess clones
      if (currentCount > desiredCount) {
        const toRemove = state.clones.splice(desiredCount);
        for (const clone of toRemove) clone.dispose(false, false);
        return;
      }

      // Add new clones to reach desiredCount
      const scale = this.computeHumanScale(bounds);
      const positions = this.scatterFromRoomCenter(bounds, desiredCount, standY);

      for (let i = currentCount; i < desiredCount; i++) {
        const clone = this.createHumanClone(roomId, i, scale);
        if (!clone) break;
        clone.parent = state.parentNode;
        const pos = positions[i] ?? positions[positions.length - 1];
        const perAvatarBottomY = (clone as any)._avatarBottomLocalY ?? this.templateBottomLocalY;
        const perAvatarFeetOffset = -perAvatarBottomY * ((clone as any).scaling?.y ?? scale);
        clone.position = new Vector3(pos.x, pos.y + perAvatarFeetOffset, pos.z);
        clone.setEnabled(true);
        this.enableChildMeshes(clone);
        state.clones.push(clone);
      }
    }
  }

  private clearRoom(roomId: string): void {
    this.removeCluster(roomId);
    const state = this.roomStates.get(roomId);
    if (!state) return;
    for (const clone of state.clones) clone.dispose(false, false);
    state.parentNode.dispose();
    this.roomStates.delete(roomId);
  }

  /** Dispose all avatar clones for a room without removing the parent node. */
  private clearAvatarClones(state: RoomAssetState): void {
    for (const clone of state.clones) clone.dispose(false, false);
    state.clones = [];
  }

  /** Creates a cluster: a semi-transparent sphere with a dynamic count label
   * floating above, and a representative avatar standing beneath it.  */
  private createCluster(
    roomId: string,
    count: number,
    center: Vector3,
    bounds: { min: Vector3; max: Vector3 },
    state: RoomAssetState
  ): void {
    if (!this.scene) return;

    const scale = this.computeHumanScale(bounds);
    const avatar = this.createHumanClone(roomId, -1, scale);
    if (!avatar) {
      console.warn(`[live-user-asset] Skipping cluster for room ${roomId}: avatar GLB unavailable`);
      return;
    }

    avatar.parent = state.parentNode;
    avatar.setEnabled(true);
    this.enableChildMeshes(avatar);

    const sphereRadius = this.config.clusterSphereRadius;
    const sphere = MeshBuilder.CreateSphere(
      `cluster_${roomId}`,
      { diameter: sphereRadius * 2, segments: 16 },
      this.scene
    );
    sphere.parent = state.parentNode;

    const sphereMat = new StandardMaterial(`clusterSphereMat_${roomId}`, this.scene);
    sphereMat.diffuseColor = new Color3(1, 1, 1);
    sphereMat.emissiveColor = new Color3(1, 1, 1);
    sphereMat.alpha = 0.15;
    sphereMat.backFaceCulling = false;
    sphere.material = sphereMat;

    const label = MeshBuilder.CreatePlane(
      `cluster_label_${roomId}`,
      { size: sphereRadius * 1.6 },
      this.scene
    );
    label.parent = sphere;
    label.position = Vector3.Zero();
    label.billboardMode = Mesh.BILLBOARDMODE_ALL;

    const texture = new DynamicTexture(
      `cluster_tex_${roomId}`,
      { width: 256, height: 256 },
      this.scene,
      false
    );
    const statusCounts = this.roomStatusCounts.get(roomId) ?? { danger: 0, safe: 0, unknown: count };
    this.drawClusterRingAndLabel(texture, count, statusCounts);

    const labelMat = new StandardMaterial(`cluster_mat_${roomId}`, this.scene);
    labelMat.diffuseTexture = texture;
    labelMat.opacityTexture = texture;
    labelMat.emissiveColor = Color3.White();
    labelMat.backFaceCulling = false;
    label.material = labelMat;

    const cluster: ClusterState = {
      sphere,
      label,
      labelTexture: texture,
      avatar,
      count,
      layoutCenter: center.clone(),
      layoutScale: scale,
      layoutCeilingY: bounds.max.y,
    };
    this.layoutClusterVisuals(cluster, center, scale, avatar);
    this.roomClusters.set(roomId, cluster);
  }

  /** Updates the count label on an existing cluster; ensures avatar exists and stays aligned. */
  private updateClusterCount(
    roomId: string,
    cluster: ClusterState,
    count: number,
    center: Vector3,
    bounds: { min: Vector3; max: Vector3 },
    state: RoomAssetState
  ): void {
    const statusCounts = this.roomStatusCounts.get(roomId) ?? { danger: 0, safe: 0, unknown: count };
    cluster.count = count;
    this.drawClusterRingAndLabel(cluster.labelTexture, count, statusCounts);

    const scale = this.computeHumanScale(bounds);
    if (!cluster.avatar || cluster.avatar.isDisposed()) {
      cluster.avatar = this.createHumanClone(roomId, -1, scale);
      if (cluster.avatar) {
        cluster.avatar.parent = state.parentNode;
        cluster.avatar.setEnabled(true);
        this.enableChildMeshes(cluster.avatar);
      }
    }

    if (!cluster.avatar) {
      this.removeCluster(roomId);
      return;
    }

    cluster.layoutCenter = center.clone();
    cluster.layoutScale = scale;
    cluster.layoutCeilingY = bounds.max.y;
    this.layoutClusterVisuals(cluster, center, scale, cluster.avatar);
  }

  private layoutClusterVisuals(
    cluster: ClusterState,
    center: Vector3,
    scale: number,
    avatar: AbstractMesh
  ): void {
    const avatarBottomLocalY = (avatar as any)._avatarBottomLocalY ?? this.templateBottomLocalY;
    const avatarScaleY = avatar.scaling?.y ?? scale;
    const feetYOffset = -avatarBottomLocalY * avatarScaleY;
    const avatarY = center.y + feetYOffset;

    avatar.position = new Vector3(center.x, avatarY, center.z);
    avatar.setEnabled(true);
    this.enableChildMeshes(avatar);

    const ceilingY = cluster.layoutCeilingY ?? (avatarY + this.getAvatarScaledHeight(avatar, scale));
    const gap = this.config.clusterHeadOffset;
    const sphereRadius = this.config.clusterSphereRadius;
    const sphereCentreY = ceilingY + gap + sphereRadius;

    cluster.sphere.position = new Vector3(center.x, sphereCentreY, center.z);
    cluster.sphere.scaling = new Vector3(1.5, 1.5, 1.5);
    cluster.label.scaling = new Vector3(1.5, 1.5, 1.5);
    cluster.sphere.setEnabled(true);
    cluster.label.setEnabled(true);
  }

  private getAvatarScaledHeight(avatar: AbstractMesh, fallbackScale: number): number {
    const nativeHeight = (avatar as any)._avatarNativeHeight ?? this.templateNativeHeight;
    const avatarScaleY = avatar.scaling?.y ?? fallbackScale;
    return Math.max(nativeHeight * avatarScaleY, 0.001);
  }

  private clampCenterToRoomFootprint(
    bounds: { min: Vector3; max: Vector3 },
    standY: number
  ): Vector3 {
    const margin = this.config.placementMargin;
    const rangeX = bounds.max.x - bounds.min.x;
    const rangeZ = bounds.max.z - bounds.min.z;
    const innerMinX = bounds.min.x + rangeX * margin;
    const innerMaxX = bounds.max.x - rangeX * margin;
    const innerMinZ = bounds.min.z + rangeZ * margin;
    const innerMaxZ = bounds.max.z - rangeZ * margin;
    const cx = (bounds.min.x + bounds.max.x) / 2;
    const cz = (bounds.min.z + bounds.max.z) / 2;
    const x = innerMinX <= innerMaxX ? Math.max(innerMinX, Math.min(innerMaxX, cx)) : cx;
    const z = innerMinZ <= innerMaxZ ? Math.max(innerMinZ, Math.min(innerMaxZ, cz)) : cz;
    return new Vector3(x, standY, z);
  }

  /** Redraws the number on a DynamicTexture. */
  private drawClusterRingAndLabel(
  texture: DynamicTexture,
  count: number,
  statusCounts: { danger: number; safe: number; unknown: number }
): void {
  texture.clear();
  const ctx = texture.getContext() as CanvasRenderingContext2D;
  const size = texture.getSize().width;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size * 0.46;
  const holeR = size * 0.28;

  const total = statusCounts.danger + statusCounts.safe + statusCounts.unknown;
  const safeTotal = total > 0 ? total : 1;
  const segments = [
    { color: '#ef4444', value: statusCounts.danger },
    { color: '#22c55e', value: statusCounts.safe },
    { color: '#eab308', value: statusCounts.unknown },
  ].filter(s => s.value > 0);

  // Draw each pie segment as a filled wedge, then punch out the hole
  let startAngle = -Math.PI / 2;
  for (const seg of segments) {
    const sweep = (seg.value / safeTotal) * Math.PI * 2;
    const endAngle = startAngle + sweep;

    // Filled wedge from center out
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, outerR, startAngle, endAngle);
    ctx.closePath();
    ctx.fillStyle = seg.color;
    ctx.fill();

    startAngle = endAngle;
  }

  // Punch transparent hole in center using destination-out
  ctx.globalCompositeOperation = 'destination-out';
  ctx.beginPath();
  ctx.arc(cx, cy, holeR, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,1)';
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';

  texture.update(false);
  // Draw count number in the hole
  texture.drawText(String(count), null, null, 'bold 80px Arial', '#333333', 'transparent', true);
  texture.update();
}

  private removeCluster(roomId: string): void {
  const cluster = this.roomClusters.get(roomId);
  if (!cluster) return;
  cluster.sphere.dispose();
  cluster.label.dispose();
  cluster.labelTexture.dispose();
  if (cluster.avatar) cluster.avatar.getChildMeshes(false).forEach(m => m.dispose());
  if (cluster.avatar) cluster.avatar.dispose();
  this.roomClusters.delete(roomId);
  this.roomStatusCounts.delete(roomId); // ADD THIS
}

  private createHumanClone(roomId: string, index: number, scale: number): AbstractMesh | null {
    if (this.avatarContainers.length === 0 || !this.scene) return null;

    const name = `liveUser_${roomId}_${index}`;
    const container = this.avatarContainers[
      Math.floor(Math.random() * this.avatarContainers.length)
    ];

    const entries = container.instantiateModelsToScene(
      (sourceName) => `${name}_${sourceName}`,
      false
    );

    const root = entries.rootNodes[0] as AbstractMesh;
    if (!root) return null;

    const nativeHeight = this.avatarContainerHeights.get(container) || this.templateNativeHeight;
    const bottomLocalY = this.avatarContainerBottoms.get(container) || this.templateBottomLocalY;

    const avatarScale = nativeHeight > 0.001
      ? scale * (this.templateNativeHeight / nativeHeight)
      : scale;

    root.scaling = new Vector3(avatarScale, avatarScale, avatarScale);
    (root as any)._avatarBottomLocalY = bottomLocalY;
    (root as any)._avatarNativeHeight = nativeHeight;
    root.isPickable = true;

    const mat = this.getColorMaterial();
    if (mat) {
      root.getChildMeshes(false).forEach(mesh => {
        mesh.material = mat;
      });
    } else {
      root.getChildMeshes(false).forEach(mesh => {
        mesh.isPickable = true;
      });
    }

    return root;
  }

  private measureTemplateBottomLocalY(meshes: AbstractMesh[]): number {
    let minY = Infinity;
    for (const mesh of meshes) {
      if (!mesh.geometry || !mesh.getBoundingInfo) continue;
      minY = Math.min(minY, mesh.getBoundingInfo().boundingBox.minimum.y);
    }
    return isFinite(minY) ? minY : 0;
  }

  private measureTemplateHeight(meshes: AbstractMesh[]): number {
    let minY = Infinity;
    let maxY = -Infinity;
    for (const mesh of meshes) {
      if (!mesh.geometry || !mesh.getBoundingInfo) continue;
      const bb = mesh.getBoundingInfo().boundingBox;
      minY = Math.min(minY, bb.minimum.y);
      maxY = Math.max(maxY, bb.maximum.y);
    }
    const height = maxY - minY;
    return height > 0.001 ? height : 1.8;
  }

  private computeHumanScale(roomBounds: { min: Vector3; max: Vector3 }): number {
    const roomHeight = Math.max(roomBounds.max.y - roomBounds.min.y, 0.001);

    let targetHeight = roomHeight * this.config.roomHeightFraction;
    targetHeight = Math.max(0.6, targetHeight);

    const nativeHeight = this.templateNativeHeight > 0.001 ? this.templateNativeHeight : 1.8;
    const rawScale = targetHeight / nativeHeight;
    return Math.max(this.config.minScale, Math.min(this.config.maxScale, rawScale));
  }

  private computeRoomBounds(meshes: AbstractMesh[]): { min: Vector3; max: Vector3 } | null {
    if (!meshes.length) return null;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const mesh of meshes) {
      if (!mesh.geometry) continue;
      mesh.computeWorldMatrix(true);
      mesh.refreshBoundingInfo(true, false);
      const bb = mesh.getBoundingInfo().boundingBox;
      minX = Math.min(minX, bb.minimumWorld.x);
      minY = Math.min(minY, bb.minimumWorld.y);
      minZ = Math.min(minZ, bb.minimumWorld.z);
      maxX = Math.max(maxX, bb.maximumWorld.x);
      maxY = Math.max(maxY, bb.maximumWorld.y);
      maxZ = Math.max(maxZ, bb.maximumWorld.z);
    }
    if (minX === Infinity) return null;
    return { min: new Vector3(minX, minY, minZ), max: new Vector3(maxX, maxY, maxZ) };
  }

  private resolveStandWorldY(
    meshes: AbstractMesh[],
    union: { min: Vector3; max: Vector3 }
  ): number {
    const unionMidY = (union.min.y + union.max.y) / 2;
    const slabs: number[] = [];

    for (const mesh of meshes) {
      if (!mesh.geometry) continue;
      mesh.computeWorldMatrix(true);
      mesh.refreshBoundingInfo(true, false);
      const bb = mesh.getBoundingInfo().boundingBox;
      const sx = Math.abs(bb.maximumWorld.x - bb.minimumWorld.x);
      const sy = Math.abs(bb.maximumWorld.y - bb.minimumWorld.y);
      const sz = Math.abs(bb.maximumWorld.z - bb.minimumWorld.z);
      const horiz = Math.sqrt(Math.max(sx * sz, 1e-8));
      const isFlat = sy < Math.max(horiz * 0.4, 0.08) && sy < sx && sy < sz;
      const topY = bb.maximumWorld.y;
      if (isFlat && topY <= unionMidY) slabs.push(topY);
    }

    if (slabs.length) return Math.max(...slabs);
    return union.min.y;
  }

  private scatterFromRoomCenter(
    bounds: { min: Vector3; max: Vector3 },
    count: number,
    standY: number
  ): Vector3[] {
    const margin = this.config.placementMargin;
    const rangeX = bounds.max.x - bounds.min.x;
    const rangeZ = bounds.max.z - bounds.min.z;
    const innerMinX = bounds.min.x + rangeX * margin;
    const innerMaxX = bounds.max.x - rangeX * margin;
    const innerMinZ = bounds.min.z + rangeZ * margin;
    const innerMaxZ = bounds.max.z - rangeZ * margin;
    const cx = (bounds.min.x + bounds.max.x) / 2;
    const cz = (bounds.min.z + bounds.max.z) / 2;
    const innerW = innerMaxX - innerMinX;
    const innerD = innerMaxZ - innerMinZ;
    const safeCx = innerW >= 1e-6 ? Math.max(innerMinX, Math.min(innerMaxX, cx)) : cx;
    const safeCz = innerD >= 1e-6 ? Math.max(innerMinZ, Math.min(innerMaxZ, cz)) : cz;

    const clampXZ = (x: number, z: number): [number, number] => {
      if (innerMinX <= innerMaxX && innerMinZ <= innerMaxZ) {
        return [Math.max(innerMinX, Math.min(innerMaxX, x)),
                Math.max(innerMinZ, Math.min(innerMaxZ, z))];
      }
      return [safeCx, safeCz];
    };

    if (count <= 0) return [];
    if (count === 1) {
      const [x, z] = clampXZ(cx, cz);
      return [new Vector3(x, standY, z)];
    }

    const maxRadius = Math.min(Math.max(innerW, 1e-8), Math.max(innerD, 1e-8)) / 2;
    const out: Vector3[] = [];
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < count; i++) {
      const t = (i + 0.5) / count;
      const r = maxRadius * 0.92 * Math.sqrt(t);
      const jitterScale = 0.15 * maxRadius;
      const theta = i * goldenAngle + (Math.random() - 0.5) * 0.35;
      let x = cx + r * Math.cos(theta) + (Math.random() - 0.5) * jitterScale;
      let z = cz + r * Math.sin(theta) + (Math.random() - 0.5) * jitterScale;
      [x, z] = clampXZ(x, z);
      out.push(new Vector3(x, standY, z));
    }
    return out;
  }
  
  private getColorMaterial(): StandardMaterial | null {
    if (!this.config.color) return null;
    if (this.colorMaterial) return this.colorMaterial;
    if (!this.scene) return null;
    this.colorMaterial = new StandardMaterial('liveUser_color', this.scene);
    this.colorMaterial.diffuseColor = this.config.color;
    this.colorMaterial.specularColor = Color3.Black();
    this.colorMaterial.backFaceCulling = false;
    return this.colorMaterial;
  }

  private applyColorToExistingClones(): void {
    this.colorMaterial?.dispose();
    this.colorMaterial = null;
    const mat = this.getColorMaterial();
    for (const state of this.roomStates.values()) {
      for (const clone of state.clones) {
        clone.getChildMeshes(false).forEach(child => { if (mat) child.material = mat; });
      }
    }
  }

  private enableChildMeshes(root: AbstractMesh): void {
    root.getChildMeshes(false).forEach(child => {
      child.setEnabled(true);
      child.isPickable = true;
    });
  }
}